import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import {
    AumicoCoreProvider,
    Constants,
    DateLocalePipe,
    GICSCodesUtil,
    ITableOptions,
    NotificationType,
} from '@modeso/aumico-lib-core-fe';
import { ICompanyDTO, ITagDTO } from '@modeso/types__aumico-lib-companies-be';
import { INameId } from '@modeso/types__aumico-lib-core-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Actions, ofType } from '@ngrx/effects';
import { ActionsSubject, Store } from '@ngrx/store';
import { I18NextService } from 'angular-i18next';
import { cloneDeep } from 'lodash-es';
import { AssigneeFilter } from 'projects/aumico-project-reports/src/app/enums/assigneeFilter.enum';
import { BehaviorSubject, combineLatest, filter } from 'rxjs';
import { HamButtonComponent } from '../../../../../aumico-components/src/lib';
import {
    bulkCreateFinancialYear,
    BulkCreateFinancialYearJobStatusDto,
    bulkCreateFinancialYearPreview,
    BulkCreateFinancialYearPreviewDto,
    BulkCreateFinancialYearResponseDto,
    BulkCreateStatus,
    onBulkCreateFinancialYearFailed,
    onBulkCreateFinancialYearPreviewFailed,
    onBulkCreateFinancialYearPreviewSuccessfully,
    onBulkCreateFinancialYearSuccessfully,
    onBulkCreateJobCompleted,
    onBulkCreateJobFailed,
    onBulkCreateStatusUpdate,
    stopPollingBulkCreateStatus,
} from '../../../../../aumico-lib-annualreport-fe/src/public-api';
import {
    clearDoNotShowAgainMessages
} from '../../../../../aumico-lib-cpmm-fe/src/lib/actions/ui.actions';
import * as licenceActions from '../../../../../aumico-lib-users-fe/src/lib/actions/licence.actions';
import * as userActions from '../../../../../aumico-lib-users-fe/src/lib/actions/user.actions';
import { BannersEnum } from '../../../../../aumico-lib-users-fe/src/lib/enums/banners.enum';
import { CPMMState } from '../../../../../aumico-lib-users-fe/src/lib/enums/cpmm-state.enum';
import { OrganizationState } from '../../../../../aumico-lib-users-fe/src/lib/enums/organization-state.enum';
import { GetUserDto, OrganizationProvider } from '../../../../../aumico-lib-users-fe/src/public-api';
import { onBulkUpdateCompaniesFailed, onBulkUpdateCompaniesSuccessfully, onUpdateCompanyFailed, onUpdateCompanySuccessfully } from '../../actions/companies.actions';
import { setCompaniesFilter } from '../../actions/ui.actions';
import { localizationKeys } from '../../constants/localizationKeys';
import { ICompanyExportDto } from '../../dtos/company-export-dto.interface';
import { ICompanyExportTableHeaders } from '../../dtos/company-export-table-header-dto.interface';
import { TasksUpdate } from '../../enums/tasks-update.enum';
import { CompaniesFilter } from '../../interfaces/companies-filter.interface';
import { CompaniesProvider } from '../../provider/companies.provider';
import { TagsProvider } from '../../provider/tags.provider';
import { selectCompaniesFilter } from '../../reducer/ui.reducer';

interface TableFields {
    _id: string;
    name: string;
    label: string;
    street: string;
    location: string;
    zip: string;
    assignee: string;
    clerk?: string;
    assigneeDeleted: boolean;
    clerkDeleted: boolean;
    status: string;
    workflow?: string;
    /** REV-22155: tag names, ready to show; a deleted tag is already dropped. */
    tagNames: string[];
}

@UntilDestroy()
@Component({
    selector: 'aumico-companies-list2',
    templateUrl: 'companies-list2.component.html',
    styleUrls: ['companies-list2.component.scss'],
})
export class CompaniesList2Component implements OnInit, AfterViewInit {
    private static readonly SCROLL_POS_KEY = 'company_list_scroll_pos';
    private hasRestoredScroll = false;

    displayArchived: boolean = false;
    isLicenseBannerDismissed: boolean = false;
    activeCompanyNumber: number;
    activeLicenses: string;
    archivedCompanyNumber: number;
    showConfirmationDialog = false;
    openedAssigneeFlyoutCompanyId: string | null = null;
    openedClerkFlyoutCompanyId: string | null = null;
    flyoutPosition: { top: number; right?: number; left?: number; isAbove?: boolean };
    @ViewChild('assigneeButtonWrapper') assigneeButtonWrapper: ElementRef;
    @ViewChild('clerkButtonWrapper') clerkButtonWrapper: ElementRef;
    @ViewChild('pageWrapper') pageWrapper!: ElementRef;
    organizationHasCpmmActive: boolean = false;
    organizationHasMandateAndTaskManagementEnabled: boolean = false;

    @Input() public set injectedProviderSetter(injectedProvider: CompaniesProvider) {
        if (injectedProvider) {
            this.provider = injectedProvider;
        }
    }
    @Input() userPreferredNumberOfDocumentsPerPage: number | undefined;

    @Input() set dismissedBanners(value: BannersEnum[]) {
        if (value.includes(BannersEnum.MAX_LISCENSE)) {
            this.isLicenseBannerDismissed = true;
        }
    }
    isInTrialMode: boolean = false;
    isSuspended: boolean = false;
    isChurned: boolean = false;
    churnDate: Date | null = null;
    protected subscriptions = [];
    options: ITableOptions;
    local = localizationKeys;
    companies: ICompanyDTO[] = [];
    private _filterText: string = '';
    private _originalData: TableFields[] = [];
    filteredData: TableFields[];
    @Input() currentLanguage = 'de-ch';
    @Input() defaultLanguage = 'de-ch';

    _organizationUsers: INameId[] = [];
    deletedOrganizationUsers: INameId[] = []; // Only deleted users for checking deleted status

    @Input() set organizationUsers(value: INameId[]) {
        if (value != null) {
            this._organizationUsers = cloneDeep(value);
            this.searchedMandateManagersOptions = this.promoteCurrentUserToTop(cloneDeep(value));

            const unassigned = {
                id: AssigneeFilter.unassigned,
                name: this.i18next.t('aumico_tasks_unassigned') as string,
            };
            const insertAt = Math.min(1, this.searchedMandateManagersOptions.length);
            this.searchedMandateManagersOptions.splice(insertAt, 0, unassigned);
        }
    }
    get organizationUsers(): INameId[] {
        return this._organizationUsers;
    }

    private promoteCurrentUserToTop(users: INameId[]): INameId[] {
        if (!this.currentUserId || !users?.length) {
            return users ?? [];
        }
        const idx = users.findIndex((u) => u.id === this.currentUserId);
        if (idx <= 0) {
            return users;
        }
        const [me] = users.splice(idx, 1);
        users.unshift(me);
        return users;
    }

    @Input() currentUserId: string | null = null;

    searchedMandateManagersOptions: INameId[] = [];

    private _workflowOptions$ = new BehaviorSubject<INameId[]>([]);

    @Input() set workflowOptions(value: INameId[]) {
        this._workflowOptions$.next(value || []);
    }

    get workflowOptions(): INameId[] {
        return this._workflowOptions$.value;
    }

    @Output() editButtonClicked: EventEmitter<string> = new EventEmitter();

    @Output() numberOfDocumentsPerPageChanged = new EventEmitter<number>();
    @Output() search = new EventEmitter<string>();
    @Output() new = new EventEmitter<void>();

    private industryOptions: INameId[] = [];

    openAssigneeChangeConfirmationDialog = false;
    openClerkChangeConfirmationDialog = false;
    selectedCompany: TableFields | undefined;
    selectedAssigneeId: string | undefined;
    selectedClerkId: string | undefined;
    selectedTaskUpdate = TasksUpdate.UPDATE_TASKS_ASSIGNED_TO_CURRENT;
    public readonly tasksUpdate = TasksUpdate;

    // REV-22155: name, street, zip, city, tag (only with tags), Mandatsleiter, Sachbearbeiter — the Workflow column is gone.
    get columnClasses(): string[] {
        const textColumns = this.tags.length > 0 ? 6 : 5;
        return ['hamScaleCol', ...Array(textColumns).fill('hamTextCol200')];
    }
    searchForm: FormGroup;
    companiesFilter: CompaniesFilter = {};

    // REV-22155: tags of the organization — column, filter and the tag box of the form.
    tags: ITagDTO[] = [];
    /** Options of the tag filter after its own search box (its own list, not the users' one). */
    tagOptions: INameId[] = [];
    tagFilterIds: string[] = [];

    isActiveCompanyTab = true;
    status = undefined;
    private organizationName: string | undefined;

    private readonly DEFAULT_FLYOUT_HEIGHT = 200; // Default estimate
    private readonly DEFAULT_FLYOUT_WIDTH = 200; // Default estimate
    private readonly VIEWPORT_PADDING = 10; // Padding from viewport edges

    allCompaniesSelected: boolean = false;
    selectedCompanies: Set<string> = new Set<string>();

    // Range selection properties  
    private lastSelectedCompanyIndex: number | null = null;
    private flatCompanyList: Array<{ company: TableFields, companyIndex: number }> = [];

    // Variables for bulk edit
    bulkActions = [
        { id: 'ASSIGNEE_CHANGE', translationKey: 'aumico_companies_assignee_change_action_bar' },
        { id: 'CLERK_CHANGE', translationKey: 'aumico_companies_clerk_change_action_bar' },
        { id: 'CREATE_NEXT_FINANCIAL_YEAR', translationKey: 'aumico_companies_create_next_financial_year_action_bar', maxSelections: 100 },
    ];
    deleteAction = { id: 'DEACTIVATE_COMPANIES', translationKey: 'aumico_companies_deactivate_action_bar' };

    openBulkEditAssignee: boolean = false;
    bulkEditAssigneeControl = this.fb.control(null);

    openBulkEditClerk: boolean = false;
    bulkEditClerkControl = this.fb.control(null);

    openBulkDisableConfirmationDialog: boolean = false;

    // Bulk create financial year state
    showBulkCreateFinancialYearModal: boolean = false;
    bulkCreatePreviewData: BulkCreateFinancialYearPreviewDto | null = null;
    bulkCreateLoading: boolean = false;
    bulkCreateJobId: string | null = null;
    bulkCreateJobStatus: BulkCreateFinancialYearJobStatusDto | null = null;
    bulkCreateProgress: number = 0;

    get isBulkCreateInProgress(): boolean {
        const status = this.bulkCreateJobStatus?.status;
        return status === 'pending' || status === 'processing' || (this.bulkCreateJobId != null && this.bulkCreateLoading);
    }

    constructor(
        private provider: CompaniesProvider,
        private readonly organizationProvider: OrganizationProvider,
        private readonly fb: FormBuilder,
        private readonly actions$: Actions,
        private i18next: I18NextService,
        private dateLocalePipe: DateLocalePipe,
        private store: Store,
        private readonly actionsListener$: ActionsSubject,
        private coreProvider: AumicoCoreProvider,
        private readonly tagsProvider: TagsProvider,
    ) { }

    get activeColor(): string {
        if (this.status && this.status.available <= 0) {
            return 'red';
        }
        if (this.status && this.status.available <= 5) {
            return 'yellow';
        }
        if (!this.displayArchived) {
            return 'primary';
        } else {
            return 'grey';
        }
    }

    get trialMessage(): string | null {
        if (!this.status) {
            return this.i18next
                .t('aumico_licence_banner_trial_active', {
                    replace: {
                        value: '...',
                    },
                })
                .toString();
        } else if (this.status && this.status.endDate) {
            const endDate = this.dateLocalePipe.transform(new Date(this.status.endDate)).toString();
            //check if endDate is before today
            if (new Date(this.status.endDate) < new Date()) {
                return this.i18next.t('aumico_licence_banner_trial_ended').toString();
            }
            const message = this.i18next
                .t('aumico_licence_banner_trial_active', {
                    replace: {
                        value: endDate,
                    },
                })
                .toString();
            return message;
        } else {
            return '';
        }
    }

    get churnedMessage(): string | null {
        if (this.churnDate) {
            const churnedDate = this.dateLocalePipe.transform(this.churnDate).toString();
            return this.i18next
                .t('aumico_licence_banner_churned', {
                    replace: {
                        value: churnedDate,
                    },
                })
                .toString();
        }
        return null;
    }

    get canCreate(): boolean {
        if (this.status && this.status.available > 0) {
            return true;
        }
        return false;
    }

    get inactiveColor(): string {
        if (this.displayArchived) {
            return 'primary';
        } else {
            return 'grey';
        }
    }

    flyoutHeightAssignee: number = this.DEFAULT_FLYOUT_HEIGHT;

    ngAfterViewInit(): void {
        this.organizationProvider.dispatchGetLicenceStatus();

        this.actions$
            .pipe(ofType(licenceActions.licenceActions.requestLicenceSuccess), untilDestroyed(this))
            .subscribe(() => {
                this.showConfirmationDialog = true;
                setTimeout(() => {
                    this.updateTableHeight();
                }, 0);
            });

        // Calculate and set the initial table height
        setTimeout(() => {
            this.updateTableHeight();
        }, 0);

    }

    @HostListener('window:resize', [])
    onWindowResize(): void {
        this.updateTableHeight();
    }

    private updateTableHeight(): void {
        if (this.pageWrapper && this.pageWrapper.nativeElement) {
            const pageWrapperElement = this.pageWrapper.nativeElement as HTMLElement;
            const rect = pageWrapperElement.getBoundingClientRect();
            const distanceFromTop = rect.top;

            // Calculate the available height: 100vh minus the distance from top
            const staticHeightOfTopNavBar = 76; // Adjust this value based on your layout
            const availableHeight = window.innerHeight - distanceFromTop - staticHeightOfTopNavBar;
            // Set the CSS custom property with the calculated height
            // We subtract a small buffer (like 20px) to prevent scrollbars
            const tableMaxHeight = Math.max(200, availableHeight - 20); // Minimum 200px

            pageWrapperElement.style.setProperty('--ham-table-max-h', `${tableMaxHeight}px`);
        }
    }

    ngOnInit(): void {
        // Request users including deleted ones for proper display
        this.organizationProvider.dispatchGetUsers(true);
        this.loadTags();
        this.subscriptions.push(
            combineLatest([this.provider.getCompanies$(), this.organizationProvider.getUsers(), this._workflowOptions$])
                .pipe(untilDestroyed(this))
                .subscribe(([companies, users]) => {
                    if (companies == null || users == null) {
                        return;
                    }

                    // Keep full userMap for display logic (includes deleted users)
                    const userMap = new Map(users.map((user) => [user.id, user]));

                    // Split users into active and deleted arrays
                    const activeUsers = users.filter(user => !user.isDeleted && !user.isInvitee);
                    const deletedUsers = users.filter(user => user.isDeleted);

                    // Map to INameId format for dropdowns
                    const activeUserNameIds: INameId[] = activeUsers.map(user => ({
                        id: user.id,
                        name: `${user.firstName} ${user.lastName}`
                    }));

                    const deletedUserNameIds: INameId[] = deletedUsers.map(user => ({
                        id: user.id,
                        name: `${user.firstName} ${user.lastName}`
                    }));

                    // Assign active users directly (bypass setter to avoid @Input conflicts)
                    this._organizationUsers = cloneDeep(activeUserNameIds);
                    this.searchedMandateManagersOptions = this.promoteCurrentUserToTop(cloneDeep(activeUserNameIds));

                    // Add unassigned option to search options
                    const unassigned = {
                        id: AssigneeFilter.unassigned,
                        name: this.i18next.t('aumico_tasks_unassigned') as string,
                    };
                    const insertAt = Math.min(1, this.searchedMandateManagersOptions.length);
                    this.searchedMandateManagersOptions.splice(insertAt, 0, unassigned);

                    // Store deleted users separately for checking
                    this.deletedOrganizationUsers = deletedUserNameIds;

                    // Calculate flyout height based on active users only
                    this.flyoutHeightAssignee = Math.min(activeUserNameIds.length * 40 + 10, 280);

                    this.handleGetCompanies(companies, userMap);
                }),
        );

        this.searchForm = this.fb.group({
            searchTerm: [''],
        });
        this.organizationProvider.dispatchGetLicenceStatus();
        this.organizationProvider
            .getLicenceStatus$()
            .pipe(untilDestroyed(this))
            .subscribe((value) => {
                this.status = value;
                if (value != null) {
                    this.activeCompanyNumber = value.activeCompanies;
                    this.activeLicenses = `/${value.totalLicenses}`;
                    if (value.endDate && new Date(value.endDate) <= new Date()) {
                        this.churnDate = null;
                    } else {
                        this.churnDate = value.endDate;
                    }
                }
                setTimeout(() => {
                    this.updateTableHeight();
                }, 0);
            });
        this.organizationProvider
            .getCurrentOrganization$()
            .pipe(untilDestroyed(this))
            .subscribe((org) => {
                if (org == null) {
                    return;
                }
                this.organizationHasCpmmActive = org.cpmmActivationDetails?.activationState === CPMMState.ACTIVE;
                this.organizationHasMandateAndTaskManagementEnabled = org.isMandateAndTaskManagementEnabled ?? false;
                this.isInTrialMode = org.state === OrganizationState.TRIAL;
                this.isSuspended = org.state === OrganizationState.ON_HOLD;
                this.isChurned = org.state === OrganizationState.CHURNED;
                this.organizationName = org.name;
            });
        this.industryOptions = GICSCodesUtil.getAllGICSCodesAndSubindustryNames();

        this.actionsListener$
            .pipe(ofType(onUpdateCompanySuccessfully), untilDestroyed(this))
            .subscribe(() => this.onSaveSuccessfully());
        this.actionsListener$
            .pipe(ofType(onUpdateCompanyFailed), ofType(onUpdateCompanyFailed), untilDestroyed(this))
            .subscribe(() => this.onSaveFailed());

        this.actionsListener$
            .pipe(ofType(onBulkUpdateCompaniesSuccessfully), untilDestroyed(this))
            .subscribe(() => this.onBulkUpdateSuccessfully());

        this.actionsListener$
            .pipe(ofType(onBulkUpdateCompaniesFailed), untilDestroyed(this))
            .subscribe(() => this.onBulkUpdateFailed());

        // Bulk create financial year subscriptions
        this.actionsListener$
            .pipe(ofType(onBulkCreateFinancialYearPreviewSuccessfully), untilDestroyed(this))
            .subscribe((action) => this.onBulkCreatePreviewSuccess(action.payload));
        this.actionsListener$
            .pipe(ofType(onBulkCreateFinancialYearPreviewFailed), untilDestroyed(this))
            .subscribe(() => this.onBulkCreatePreviewFailed());
        this.actionsListener$
            .pipe(ofType(onBulkCreateFinancialYearSuccessfully), untilDestroyed(this))
            .subscribe((action) => this.onBulkCreateJobStarted(action.payload));
        this.actionsListener$
            .pipe(ofType(onBulkCreateFinancialYearFailed), untilDestroyed(this))
            .subscribe(() => this.onBulkCreateFailed());
        this.actionsListener$
            .pipe(ofType(onBulkCreateStatusUpdate), untilDestroyed(this))
            .subscribe((action) => this.onBulkCreateStatusUpdate(action.payload));
        this.actionsListener$
            .pipe(ofType(onBulkCreateJobCompleted), untilDestroyed(this))
            .subscribe((action) => this.onBulkCreateJobCompleted(action.payload));
        this.actionsListener$
            .pipe(ofType(onBulkCreateJobFailed), untilDestroyed(this))
            .subscribe((action) => this.onBulkCreateJobFailedHandler(action.payload));

        this.store
            .select(selectCompaniesFilter())
            .pipe(
                filter(
                    (companiesFilter) =>
                        companiesFilter != null &&
                        (companiesFilter.textSearch != null ||
                            companiesFilter.mandateManagerIds != null ||
                            companiesFilter.clerkIds != null ||
                            companiesFilter.tagIds != null),
                ),
                untilDestroyed(this),
            )
            .subscribe((companiesFilter) => {
                this.companiesFilter = { ...companiesFilter };
                if (companiesFilter.textSearch != null) {
                    this.searchForm.get('searchTerm').setValue(companiesFilter.textSearch, { emitEvent: false });
                    this.filterText = companiesFilter.textSearch;
                }

                if (companiesFilter.mandateManagerIds?.length) {
                    this.companiesFilter.mandateManagerIds = companiesFilter.mandateManagerIds;
                }
                if (companiesFilter.clerkIds?.length) {
                    this.companiesFilter.clerkIds = companiesFilter.clerkIds;
                }
                if (companiesFilter.tagIds?.length) {
                    this.companiesFilter.tagIds = companiesFilter.tagIds;
                    this.tagFilterIds = [...companiesFilter.tagIds];
                }

                this.applyFilter();
                this.search.emit();
            });
    }

    /** REV-22155: the list is loaded once; tag ids on a company are resolved to names here. */
    private loadTags(): void {
        this.tagsProvider
            .getTags$()
            .pipe(untilDestroyed(this))
            .subscribe((tags) => {
                this.tags = tags ?? [];
                this.tagOptions = this.tags.map((tag) => ({ id: tag._id, name: tag.name }));
                // Companies may already be mapped — redo the names.
                this._originalData = this._originalData.map((item) => ({
                    ...item,
                    tagNames: this.tagNamesOf(item._id),
                }));
                this.applyFilter();
            });
        this.tagsProvider.dispatchTagsList();
    }

    private tagNamesOf(companyId: string): string[] {
        const company = this.companies?.find((item) => item._id === companyId);
        return (company?.tags ?? [])
            .map((tagId) => this.tags.find((tag) => tag._id === tagId)?.name)
            .filter((name): name is string => name != null);
    }

    onTagFilterChange(tagIds: string[]): void {
        this.companiesFilter = {
            ...this.companiesFilter,
            tagIds,
        };
        this.tagFilterIds = [...tagIds];
        this.applyFilter();
        this.search.emit();
        this.store.dispatch(setCompaniesFilter({ filter: this.companiesFilter }));
    }

    onSearchFilterChangeTag(searchTerm: string): void {
        const wanted = (searchTerm ?? '').trim().toLowerCase();
        const options = this.tags.map((tag) => ({ id: tag._id, name: tag.name }));
        this.tagOptions = wanted ? options.filter((tag) => tag.name.toLowerCase().includes(wanted)) : options;
    }

    private handleGetCompanies(companies: ICompanyDTO[], users: Map<string, GetUserDto>): void {
        if (companies != null) {
            this.companies = companies;
            this._originalData = this.mapICompanyToListModel(companies, users);
            this.activeCompanyNumber = this._originalData.filter((company) => company.status === 'active').length;
            this.archivedCompanyNumber = this._originalData.filter((company) => company.status === 'inactive').length;
            this.applyFilter();
            if (!this.hasRestoredScroll) {
                this.hasRestoredScroll = true;
                setTimeout(() => this.restoreScrollPosition(), 0);
            }
        }
    }

    private mapICompanyToListModel(companies: ICompanyDTO[], users: Map<string, GetUserDto>): TableFields[] {
        return companies.map((company) => {
            const assigneeUser = company.assignee ? users.get(company.assignee) : null;
            const assignee = assigneeUser ? `${assigneeUser.firstName} ${assigneeUser.lastName}` : null;
            const clerkUser = company.clerk ? users.get(company.clerk) : null;
            const clerk = clerkUser ? `${clerkUser.firstName} ${clerkUser.lastName}` : null;

            let workflowName = '';
            if (company.workflowId != null) {
                workflowName =
                    this.workflowOptions.find((workflow) => {
                        return workflow.id === company.workflowId;
                    })?.name || '';
            }
            return {
                _id: company._id,
                name: company.name || '-',
                label: company.label,
                street: company.street || '-',
                zip: company.zip
                    ? company.country?.id
                        ? `${company.country.id}-${company.zip}`
                        : company.zip
                    : '-',
                location: company.city || '-',
                assignee,
                clerk,
                assigneeDeleted: assigneeUser?.isDeleted || false,
                clerkDeleted: clerkUser?.isDeleted || false,
                status: company.active ? 'active' : 'inactive',
                workflow: workflowName,
                tagNames: (company.tags ?? [])
                    .map((tagId) => this.tags.find((tag) => tag._id === tagId)?.name)
                    .filter((name): name is string => name != null),
            };
        });
    }

    onRowButtonClicked(company: ICompanyDTO): void {
        this.saveScrollPosition();
        this.editButtonClicked.emit(company._id);
    }

    requestMoreLicences(): void {
        this.organizationProvider.dispatchRequestMoreLicences();
    }

    dismissWarning(): void {

        this.store.dispatch(userActions.dismissBanner({ banner: BannersEnum.MAX_LISCENSE }));

        this.isLicenseBannerDismissed = true;
    }

    private getScrollContainer(): HTMLElement | null {
        return this.pageWrapper?.nativeElement?.querySelector('.table-scroll-container') ?? null;
    }

    private saveScrollPosition(): void {
        const scrollEl = this.getScrollContainer();
        if (scrollEl) {
            sessionStorage.setItem(CompaniesList2Component.SCROLL_POS_KEY, String(scrollEl.scrollTop));
        }
    }

    private restoreScrollPosition(): void {
        const saved = sessionStorage.getItem(CompaniesList2Component.SCROLL_POS_KEY);
        if (saved !== null) {
            const scrollEl = this.getScrollContainer();
            if (scrollEl) {
                const parsedValue = Number(saved);
                if (!isNaN(parsedValue)) {
                    scrollEl.scrollTop = parsedValue;
                }
                sessionStorage.removeItem(CompaniesList2Component.SCROLL_POS_KEY);
            }
        }
    }

    // For support reasons, we need to give the user the option to enable the licence banner again
    @HostListener('window:keydown', ['$event'])
    handleKeyboardShortcuts(event: KeyboardEvent) {
        // CTRL + Q -> MAC or ALT+Q: Cleare Do Not Show Again Messages
        if (event.ctrlKey && event.key.toLowerCase() === 'q') {
            event.preventDefault();
            this.store.dispatch(clearDoNotShowAgainMessages());
            return;
        }
    }

    get filterText(): string {
        return this._filterText;
    }

    set filterText(value: string) {
        this._filterText = value.toLowerCase();
        this.applyFilter();
    }

    private applyFilter(): void {
        const status = this.displayArchived ? 'inactive' : 'active';
        this.filteredData = this._originalData.filter((item) => item.status === status);

        if (this.companiesFilter.mandateManagerIds?.length) {
            const filteredCompaniesWithAssigneeIds = this.companies.filter((item) =>
                this.companiesFilter.mandateManagerIds.includes(item.assignee),
            );

            this.filteredData = this.filteredData.filter((item) =>
                filteredCompaniesWithAssigneeIds.some((company) => company._id === item._id),
            );

            // If filtered for unassigned, include companies without assignee
            if (this.companiesFilter.mandateManagerIds.includes(AssigneeFilter.unassigned)) {
                this.filteredData = this.filteredData.concat(
                    this._originalData.filter((item) => {
                        const company = this.companies.find((c) => c._id === item._id);
                        return !company.assignee;
                    }),
                );
            }
        }
        if (this.companiesFilter.clerkIds?.length) {
            const filteredCompaniesWithClerkIds = this.companies.filter((item) =>
                this.companiesFilter.clerkIds.includes(item.clerk),
            );
            this.filteredData = this.filteredData.filter((item) =>
                filteredCompaniesWithClerkIds.some((company) => company._id === item._id),
            );

            // If filtered for unassigned, include companies without clerk
            if (this.companiesFilter.clerkIds.includes(AssigneeFilter.unassigned)) {
                this.filteredData = this.filteredData.concat(
                    this._originalData.filter((item) => {
                        const company = this.companies.find((c) => c._id === item._id);
                        return !company.clerk;
                    }),
                );
            }
        }

        // REV-22155: a company matches when it carries at least one of the chosen tags.
        if (this.companiesFilter.tagIds?.length) {
            const tagIds = this.companiesFilter.tagIds;
            this.filteredData = this.filteredData.filter((item) => {
                const company = this.companies.find((c) => c._id === item._id);
                return (company?.tags ?? []).some((tagId) => tagIds.includes(tagId));
            });
        }

        if (this._filterText) {
            this.filteredData = this.filteredData.filter(
                (item) => item.name.toLowerCase().includes(this._filterText) && item.status === status || item.label?.toLowerCase().includes(this._filterText) && item.status === status
            );

        }

        // Rebuild flat list when data changes
        this.buildFlatCompanyList();

        // Update table height after content is rendered
        setTimeout(() => this.updateTableHeight(), 0);
    }

    displayActiveCompanies() {
        this.displayArchived = false;
        this.applyFilter();
    }

    displayArchivedCompanies() {
        this.displayArchived = true;
        this.applyFilter();
    }

    onPageSizeChanged(pageSize: number): void {
        this.numberOfDocumentsPerPageChanged.emit(pageSize);
    }

    onNew(): void {
        this.new.emit();
    }

    resetFilter(): void {
        this.filterText = '';
        this.companiesFilter = {
            ...this.companiesFilter,
            textSearch: undefined,
        };
        this.store.dispatch(setCompaniesFilter({ filter: this.companiesFilter }));
    }

    filterItems(): void {
        this.filterText = this.searchForm.get('searchTerm').value;
        this.applyFilter();
        this.companiesFilter = {
            ...this.companiesFilter,
            textSearch: this.filterText,
        };
        this.search.emit();
        this.store.dispatch(setCompaniesFilter({ filter: this.companiesFilter }));
    }

    onMandateManagerFilterChange(userIds: string[]): void {
        this.companiesFilter = {
            ...this.companiesFilter,
            mandateManagerIds: userIds,
        };
        this.applyFilter();
        this.search.emit();

        this.store.dispatch(setCompaniesFilter({ filter: this.companiesFilter }));
    }
    onClerkFilterChange(userIds: string[]): void {
        this.companiesFilter = {
            ...this.companiesFilter,
            clerkIds: userIds,
        };
        this.applyFilter();
        this.search.emit();
    }

    onSearchFilterChangeMandateManager(searchTerm: string): void {
        if (searchTerm === '' || searchTerm === null) {
            this.searchedMandateManagersOptions = this.promoteCurrentUserToTop(cloneDeep(this.organizationUsers));
            return;
        }
        const filtered = this.organizationUsers.filter((company) =>
            company.name.toLowerCase().includes(searchTerm.toLowerCase()),
        );
        this.searchedMandateManagersOptions = this.promoteCurrentUserToTop(filtered);
    }

    onSearchFilterChangeClerk(searchTerm: string): void {
        if (searchTerm === '' || searchTerm === null) {
            this.searchedMandateManagersOptions = this.promoteCurrentUserToTop(cloneDeep(this.organizationUsers));
            return;
        }
        const filtered = this.organizationUsers.filter((company) =>
            company.name.toLowerCase().includes(searchTerm.toLowerCase()),
        );
        this.searchedMandateManagersOptions = this.promoteCurrentUserToTop(filtered);
    }

    public onExportToXLSX(): void {
        const tableHeaders: ICompanyExportTableHeaders = {
            name: this.i18next.t('aumico_companies_export_excel_table_header_name').toString(),
            street: this.i18next.t('aumico_companies_export_excel_table_header_street_and_number').toString(),
            additionToAddress: this.i18next
                .t('aumico_companies_export_excel_table_header_addition_to_address')
                .toString(),
            zip: this.i18next.t('aumico_companies_export_excel_table_header_zip_code').toString(),
            city: this.i18next.t('aumico_companies_export_excel_table_header_city').toString(),
            country: this.i18next.t('aumico_companies_export_excel_table_header_country').toString(),
            legalform: this.i18next.t('aumico_companies_export_excel_table_header_legal_form').toString(),
            email: this.i18next.t('aumico_companies_export_excel_table_header_email').toString(),
            phone: this.i18next.t('aumico_companies_export_excel_table_header_phone').toString(),
            UID: this.i18next.t('aumico_companies_export_excel_table_header_uid_number').toString(),
            taxDomicile: this.i18next.t('aumico_companies_export_excel_table_header_tax_domicile').toString(),
            assignee: this.i18next.t('aumico_companies_export_excel_table_header_assigned_user').toString(),
            clerk: this.i18next.t('aumico_companies_export_excel_table_header_clerk').toString(),
            GICSCode: this.i18next.t('aumico_companies_export_excel_table_header_industry').toString(),
            firstName: this.i18next.t('aumico_companies_export_excel_table_header_first_name').toString(),
            lastName: this.i18next.t('aumico_companies_export_excel_table_header_last_name').toString(),
            workFlow: this.i18next.t('aumico_companies_export_excel_table_header_closing_workflow').toString(),
        };

        const companiesDataToExport: ICompanyExportDto[] = this.companies?.map((company) => ({
            name: company.name || '',
            street: company.street || '',
            additionToAddress: company.additionToAddress || '',
            zip: company.zip || '',
            city: company.city || '',
            country: company.country?.name || '',
            legalform: company.legalform?.text[this.currentLanguage] || '',
            email: company.email || '',
            phone: company.phone || '',
            UID: company.UID || '',
            taxDomicile: company.taxDomicile || null,
            assignee:
                this.organizationUsers.find((user) => {
                    return user.id === company.assignee;
                })?.name || '',
            clerk:
                this.organizationUsers.find((user) => {
                    return user.id === company.clerk;
                })?.name || '',
            GICSCode: this.getCompanyIndustry(company.GICSCode),
            firstName: company.firstName || '',
            lastName: company.lastName || '',
            workFlow:
                this.workflowOptions.find((workflow) => {
                    return workflow.id === company.workflowId;
                })?.name || '',
        }));
        const now = new Date();
        const mm = String(now.getMonth() + 1).padStart(2, '0');
        const dd = String(now.getDate()).padStart(2, '0');
        const yyyy = now.getFullYear();
        const date = `${dd}_${mm}_${yyyy}`;

        const filename = `${this.i18next.t('aumico_reports_export_all_companies_file_name').toString()}_${this.organizationName
            }_${date}`;
        this.provider.dispatchExportCompaniesDataAsExcel(tableHeaders, companiesDataToExport, filename);
    }

    private getCompanyIndustry(companyGicsCode: string): string {
        const industry = this.industryOptions.find((industry) => industry.id === companyGicsCode);
        if (industry != null && industry.id !== '0') {
            return industry.name;
        }
        return '';
    }

    openAssigneeFlyout(companyId: string, buttonComponent: HamButtonComponent): void {
        if (this.openedAssigneeFlyoutCompanyId === companyId) {
            this.closeAssigneeFlyout();
            return;
        }
        const buttonElement = buttonComponent.nativeButton?.nativeElement;

        if (!buttonElement) return;

        const rect = buttonElement.getBoundingClientRect();
        const position = this.calculateFlyoutPosition(rect, undefined, 140, this.flyoutHeightAssignee);

        this.flyoutPosition = {
            top: position.top,
            right: position.right,
            left: position.left,
            isAbove: position.isAbove,
        };
        // Then, set the ID to show the flyout
        this.openedAssigneeFlyoutCompanyId = companyId;
    }

    openClerkFlyout(companyId: string, buttonComponent: HamButtonComponent): void {
        if (this.openedClerkFlyoutCompanyId === companyId) {
            this.closeClerkFlyout();
            return;
        }
        const buttonElement = buttonComponent.nativeButton?.nativeElement;

        if (!buttonElement) return;

        const rect = buttonElement.getBoundingClientRect();
        const position = this.calculateFlyoutPosition(rect, undefined, 140, this.flyoutHeightAssignee);

        this.flyoutPosition = {
            top: position.top,
            right: position.right,
            left: position.left,
            isAbove: position.isAbove,
        };
        // Then, set the ID to show the flyout
        this.openedClerkFlyoutCompanyId = companyId;
    }
    closeAssigneeFlyout(): void {
        this.openedAssigneeFlyoutCompanyId = null;
    }

    closeClerkFlyout(): void {
        this.openedClerkFlyoutCompanyId = null;
    }

    confirmAssigneeChange(company: TableFields, assigneeId: string): void {
        this.selectedCompany = company;
        this.selectedAssigneeId = assigneeId;

        this.selectedTaskUpdate = TasksUpdate.UPDATE_ALL_TASKS;
        this.updateAssignee();
    }

    closeAssigneeChangeConfirmationDialog(): void {
        this.openAssigneeChangeConfirmationDialog = false;
        this.selectedCompany = undefined;
        this.selectedAssigneeId = undefined;
        this.selectedTaskUpdate = undefined;
    }

    updateAssignee(): void {
        const companyToUpdate = this.companies.find((c) => c._id === this.selectedCompany._id);
        if (companyToUpdate) {
            const update = { ...companyToUpdate, assignee: this.selectedAssigneeId };
            this.provider.dispatchUpdateCompany(update, null, this.selectedTaskUpdate);
        }
        this.closeAssigneeChangeConfirmationDialog();
        this.closeAssigneeFlyout();
    }

    confirmClerkChange(company: TableFields, clerkId: string): void {
        this.selectedCompany = company;
        this.selectedClerkId = clerkId;

        if (this.organizationHasCpmmActive) {
            this.openClerkChangeConfirmationDialog = true;
        } else {
            this.selectedTaskUpdate = TasksUpdate.UPDATE_ALL_TASKS;
            this.updateClerk();
        }
    }

    closeClerkChangeConfirmationDialog(): void {
        this.openClerkChangeConfirmationDialog = false;
        this.selectedCompany = undefined;
        this.selectedClerkId = undefined;
        this.selectedTaskUpdate = undefined;
    }

    updateClerk(): void {
        if (this.bulkEditClerkControl.value) {
            this.bulkUpdateClerk();
            return;
        }
        const companyToUpdate = this.companies.find((c) => c._id === this.selectedCompany._id);
        if (companyToUpdate) {
            const update = { ...companyToUpdate, clerk: this.selectedClerkId };
            this.provider.dispatchUpdateCompany(update, null, this.selectedTaskUpdate);
        }
        this.closeClerkChangeConfirmationDialog();
        this.closeClerkFlyout();
    }

    getInitials(name: string): string {
        if (!name || name.trim() === '') {
            return '';
        }
        if (name === this.i18next.t('aumico_tasks_unassigned')) {
            return '-';
        }
        // split on any amount of whitespace, no empty strings
        const names = name.trim().split(/\s+/);
        if (names.length > 1) {
            return `${names[0][0]}${names[1][0]}`;
        }
        return names[0].substring(0, 2);
    }
    bookCall() {
        window.open('https://meetings.hubspot.com/cbartosch/discovery-call', '_blank');
    }

    private onSaveSuccessfully(): void {
        const successDescriptionLocalizationKey = 'aumico_companies_changes_saved_successfully_dialog';
        const successTitleLocalizationKey = 'aumico_reports_notification_success_title';
        this.coreProvider.dispatchAddNotification(
            successTitleLocalizationKey,
            successDescriptionLocalizationKey,
            NotificationType.SUCCESS,
        );
    }

    private onBulkUpdateSuccessfully(): void {
        const successDescriptionLocalizationKey = 'aumico_companies_bulk_changes_saved_successfully_dialog';
        const successTitleLocalizationKey = 'aumico_reports_notification_success_title';
        this.coreProvider.dispatchAddNotification(
            successTitleLocalizationKey,
            successDescriptionLocalizationKey,
            NotificationType.SUCCESS,
        );
    }

    private onBulkUpdateFailed(): void {
        const errorDescriptionLocalizationKey = Constants.ERROR_DIALOG.SOMETHING_WENT_WRONG;

        this.coreProvider.dispatchAddNotification(
            'aumico_reports_notification_error_title',
            errorDescriptionLocalizationKey,
            NotificationType.ERROR,
            3500, // 2.5 seconds
        );
    }

    private onSaveFailed(): void {
        const errorDescriptionLocalizationKey = Constants.ERROR_DIALOG.SOMETHING_WENT_WRONG;

        this.coreProvider.dispatchAddNotification(
            'aumico_reports_notification_error_title',
            errorDescriptionLocalizationKey,
            NotificationType.ERROR,
            3500, // 2.5 seconds
        );
    }

    private calculateFlyoutPosition(
        buttonRect: DOMRect,
        flyoutElement?: HTMLElement,
        flyoutWidth: number = this.DEFAULT_FLYOUT_WIDTH,
        flyoutHeight: number = this.DEFAULT_FLYOUT_HEIGHT,
    ): { top: number; left: number; right: number; isAbove: boolean } {
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // Use actual flyout dimensions if available, otherwise use defaults
        const actualWidth = flyoutElement ? flyoutElement.offsetWidth : flyoutWidth;
        const actualHeight = flyoutElement ? flyoutElement.offsetHeight : flyoutHeight;

        // Calculate if flyout fits below the button
        const spaceBelow = viewportHeight - buttonRect.bottom;
        const spaceAbove = buttonRect.top;
        const fitsBelow = spaceBelow >= actualHeight + this.VIEWPORT_PADDING;
        const fitsAbove = spaceAbove >= actualHeight + this.VIEWPORT_PADDING;

        // Determine vertical position
        let top: number;
        let isAbove = false;

        if (fitsBelow) {
            // Position below button
            top = buttonRect.bottom;
        } else if (fitsAbove) {
            // Position above button
            top = buttonRect.top - actualHeight;
            isAbove = true;
        } else {
            // If it doesn't fit either way, position where there's more space
            if (spaceBelow > spaceAbove) {
                top = buttonRect.bottom;
            } else {
                top = buttonRect.top - actualHeight;
                isAbove = true;
            }
        }

        // Calculate horizontal position
        let left = buttonRect.left;
        let right = buttonRect.right;

        // Check if flyout extends beyond right edge of viewport
        if (buttonRect.left + actualWidth > viewportWidth - this.VIEWPORT_PADDING) {
            // Align flyout's right edge with button's right edge
            left = buttonRect.right - actualWidth;
            right = buttonRect.right;
        }

        // Ensure flyout doesn't extend beyond left edge
        if (left < this.VIEWPORT_PADDING) {
            left = this.VIEWPORT_PADDING;
            right = left + actualWidth;
        }

        return { top, left, right, isAbove };
    }

    private buildFlatCompanyList(): void {
        this.flatCompanyList = [];
        this.filteredData.forEach((company, index) => {
            this.flatCompanyList.push({
                company,
                companyIndex: index
            });
        });
    }

    // Selection logic methods
    clearAllSelections(): void {
        this.selectedCompanies.clear();
        this.allCompaniesSelected = false;
        this.lastSelectedCompanyIndex = null;
    }

    onToggleCheckAllCompanies(): void {
        if (this.allCompaniesSelected) {
            this.clearAllSelections();
        } else {
            this.selectedCompanies.clear();

            this.companies.forEach((company) => {
                this.selectedCompanies.add(company._id);
            });
            this.allCompaniesSelected = true;
        }
    }

    onToggleCheckCompany(companyId: string, event?: MouseEvent): void {
        // Handle shift+click for range selection
        if (event?.shiftKey && this.lastSelectedCompanyIndex !== null) {
            this.handleShiftClick(companyId);
        } else {
            this.handleNormalClick(companyId);
        }
    }

    onCompanyCheckboxClick(companyId: string, event: MouseEvent): void {
        // Stop propagation to prevent row click
        event.stopPropagation();

        // Handle shift+click for range selection
        if (event.shiftKey && this.lastSelectedCompanyIndex !== null) {
            this.handleShiftClick(companyId);
        } else {
            this.handleNormalClick(companyId);
        }
    }

    private handleShiftClick(clickedCompanyId: string): void {
        const clickedIndex = this.flatCompanyList.findIndex(item => item.company._id === clickedCompanyId);

        if (clickedIndex === -1 || this.lastSelectedCompanyIndex === null) {
            this.handleNormalClick(clickedCompanyId);
            return;
        }

        // Determine range
        const startIndex = Math.min(this.lastSelectedCompanyIndex, clickedIndex);
        const endIndex = Math.max(this.lastSelectedCompanyIndex, clickedIndex);

        // Update last selected
        this.lastSelectedCompanyIndex = clickedIndex;

        // Get all companies in the range
        const companiesInRange = this.flatCompanyList
            .slice(startIndex, endIndex + 1)
            .map(item => item.company);

        // Check if the clicked company is currently selected
        const isClickedCompanySelected = this.selectedCompanies.has(clickedCompanyId);

        // Filter companies based on the clicked company's selection state
        const companiesToToggle = companiesInRange.filter(company => {
            const isCompanySelected = this.selectedCompanies.has(company._id);

            if (isClickedCompanySelected) {
                // Clicked company is selected → only toggle currently selected companies (to unselect them)
                return isCompanySelected;
            } else {
                // Clicked company is unselected → only toggle currently unselected companies (to select them)
                return !isCompanySelected;
            }
        });

        // Apply the changes
        companiesToToggle.forEach(company => {
            if (this.selectedCompanies.has(company._id)) {
                this.selectedCompanies.delete(company._id);
            } else {
                this.selectedCompanies.add(company._id);
            }
        });

        // Update "select all" state
        this.allCompaniesSelected = this.selectedCompanies.size === this.filteredData.length;
    }

    private handleNormalClick(companyId: string): void {
        // Update last selected position for non-shift clicks
        this.lastSelectedCompanyIndex = this.flatCompanyList.findIndex(item => item.company._id === companyId);

        // Toggle single company
        if (this.selectedCompanies.has(companyId)) {
            this.selectedCompanies.delete(companyId);
            this.allCompaniesSelected = false;
        } else {
            this.selectedCompanies.add(companyId);
        }
    }

    // Bulk actions
    onActionClicked(actionId: string): void {
        if (actionId === 'ASSIGNEE_CHANGE') {
            this.openBulkEditAssignee = true;
        } else if (actionId === 'CLERK_CHANGE') {
            this.openBulkEditClerk = true;
        } else if (actionId === 'DEACTIVATE_COMPANIES') {
            this.openBulkDisableConfirmationDialog = true;
        } else if (actionId === 'CREATE_NEXT_FINANCIAL_YEAR') {
            this.initiateBulkCreateFinancialYear();
        } else if (actionId === 'CLOSE_BULK_EDIT') {
            this.clearAllSelections();
        }
    }

    confirmBulkDisable(): void {
        this.openBulkDisableConfirmationDialog = false;
        let updatedCompanies: ICompanyDTO[] = [];
        this.selectedCompanies.forEach((companyId) => {
            const companyToUpdate = this.companies.find((c) => c._id === companyId);
            if (companyToUpdate) {
                const update = { ...companyToUpdate, active: false };
                updatedCompanies.push(update);
            }
        });
        this.provider.dispatchBulkUpdateCompanies(updatedCompanies, null, this.selectedTaskUpdate);
        this.clearAllSelections();
    }

    cancelBulkDisable(): void {
        this.openBulkDisableConfirmationDialog = false;
    }

    onEditAssigneeConfirm(): void {
        if (this.bulkEditAssigneeControl.value) {
            this.openBulkEditAssignee = false;
            // Dispatch action to update assignee for selected companies
            let updatedCompanies: ICompanyDTO[] = [];
            this.selectedCompanies.forEach((companyId) => {
                const companyToUpdate = this.companies.find((c) => c._id === companyId);
                if (companyToUpdate) {
                    const update = { ...companyToUpdate, assignee: this.bulkEditAssigneeControl.value.id };
                    updatedCompanies.push(update);
                }
            });

            this.provider.dispatchBulkUpdateCompanies(updatedCompanies, null, this.selectedTaskUpdate);

            this.bulkEditAssigneeControl.reset();
            this.clearAllSelections();
        }
    }

    onEditClerkConfirm(): void {
        if (this.bulkEditClerkControl.value) {
            this.openBulkEditClerk = false;
            if (!this.organizationHasCpmmActive) {
                this.bulkUpdateClerk();
                return;
            }
            this.openClerkChangeConfirmationDialog = true;
        }
    }

    private bulkUpdateClerk(): void {
        // Dispatch bulk update clerk action for selected companies

        let updatedCompanies: ICompanyDTO[] = [];
        this.selectedCompanies.forEach((companyId) => {
            const companyToUpdate = this.companies.find((c) => c._id === companyId);
            if (companyToUpdate) {
                const update = { ...companyToUpdate, clerk: this.bulkEditClerkControl.value.id };
                updatedCompanies.push(update);
            }
        });
        this.provider.dispatchBulkUpdateCompanies(updatedCompanies, null, this.selectedTaskUpdate);
        this.bulkEditClerkControl.reset();
        this.openClerkChangeConfirmationDialog = false;
        this.clearAllSelections();
    }

    // Bulk create financial year methods
    initiateBulkCreateFinancialYear(): void {
        const companyIds = Array.from(this.selectedCompanies);
        this.bulkCreateLoading = true;
        this.store.dispatch(
            bulkCreateFinancialYearPreview({
                payload: { companyIds },
            }),
        );
    }

    onBulkCreatePreviewSuccess(previewData: BulkCreateFinancialYearPreviewDto): void {
        this.bulkCreateLoading = false;
        this.bulkCreatePreviewData = previewData;
        this.showBulkCreateFinancialYearModal = true;
    }

    onBulkCreatePreviewFailed(): void {
        this.bulkCreateLoading = false;
        this.coreProvider.dispatchAddNotification(
            'aumico_companies_bulk_create_preview_failed',
            'aumico_companies_bulk_create_preview_failed',
            NotificationType.ERROR,
        );
    }

    onBulkCreateFinancialYearConfirm(): void {
        const skippedCompanyIds = new Set(
            (this.bulkCreatePreviewData?.previews ?? [])
                .filter(p => p.status === BulkCreateStatus.SKIP)
                .map(p => p.companyId),
        );
        const companyIds = Array.from(this.selectedCompanies).filter(id => !skippedCompanyIds.has(id));
        this.bulkCreateLoading = true;
        this.showBulkCreateFinancialYearModal = false;
        this.store.dispatch(
            bulkCreateFinancialYear({
                payload: { companyIds },
            }),
        );
    }

    onBulkCreateJobStarted(responseData: BulkCreateFinancialYearResponseDto): void {
        // Job created successfully, store jobId and keep the page interactive
        this.bulkCreateJobId = responseData.jobId;
        this.bulkCreateLoading = true;
        this.showBulkCreateFinancialYearModal = false;
        // Polling will start automatically via effect
    }

    onBulkCreateStatusUpdate(statusData: BulkCreateFinancialYearJobStatusDto): void {
        // Update job status and progress
        this.bulkCreateJobStatus = statusData;
        if (statusData.totalCompanies > 0) {
            this.bulkCreateProgress = Math.round(
                (statusData.processedCompanies / statusData.totalCompanies) * 100
            );
        }
    }

    onBulkCreateJobCompleted(statusData: BulkCreateFinancialYearJobStatusDto): void {
        this.bulkCreateLoading = false;
        this.showBulkCreateFinancialYearModal = false;
        this.bulkCreatePreviewData = null;
        this.bulkCreateJobStatus = null;
        this.bulkCreateJobId = null;
        this.bulkCreateProgress = 0;

        const message = this.i18next.t('aumico_companies_bulk_create_success', {
            created: statusData.successCount,
            failed: statusData.failureCount,
        }) as string;


        if (statusData.successCount === 0) {
            this.coreProvider.dispatchAddNotification(
                message,
                'aumico_companies_bulk_create_success',
                NotificationType.ERROR,
                4000, // Show for 4 seconds
            );
        } else {

            this.coreProvider.dispatchAddNotification(
                message,
                'aumico_companies_bulk_create_success',
                NotificationType.SUCCESS,
                4000, // Show for 4 seconds
            );
        }


        this.clearAllSelections();
    }

    onBulkCreateJobFailedHandler(statusData: BulkCreateFinancialYearJobStatusDto): void {
        this.bulkCreateLoading = false;
        this.bulkCreateJobStatus = null;
        this.bulkCreateJobId = null;
        this.bulkCreateProgress = 0;

        this.coreProvider.dispatchAddNotification(
            'aumico_companies_bulk_create_job_failed',
            'aumico_companies_bulk_create_job_failed',
            NotificationType.ERROR,
        );
    }

    onBulkCreateFailed(): void {
        this.bulkCreateLoading = false;
        this.coreProvider.dispatchAddNotification(
            'aumico_companies_bulk_create_failed',
            'aumico_companies_bulk_create_failed',
            NotificationType.ERROR,
        );
    }

    onBulkCreateFinancialYearCancel(): void {
        // If job is running, stop polling
        if (this.bulkCreateJobId) {
            this.store.dispatch(stopPollingBulkCreateStatus());
        }

        this.showBulkCreateFinancialYearModal = false;
        this.bulkCreatePreviewData = null;
        this.bulkCreateJobStatus = null;
        this.bulkCreateJobId = null;
        this.bulkCreateProgress = 0;
        this.bulkCreateLoading = false;
    }
}
